import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import ConfirmModal from '../components/ConfirmModal';
import PremiumSelect from '../components/PremiumSelect';
import PageContainer from '../components/ui/PageContainer';
import StatusChip from '../components/ui/StatusChip';
import type { StatusTone } from '../components/ui/StatusChip';
import { Cycle, AlertState, ManagerFeedbackFillStatus } from '../types';
import {
  formatDateTimeBr,
  formatCycleDateBr,
  cycleDateToInputValue,
  isCycleClosedForSubmissions,
  isCycleCurrentlyOpen,
  findCurrentActiveCycle,
  shouldHighlightTeamFeedbackRow,
  endOfCalendarDayBrazilMs,
  defaultDeadlineFromEndDate,
} from '../utils/cycleFeedbackUi';

interface CycleFormData {
  name: string;
  startDate: string;
  endDate: string;
  deadlineDate: string;
  active: boolean;
}

const KICKER_SX = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'text.secondary',
} as const;

const emptyFormData = (): CycleFormData => ({
  name: '',
  startDate: '',
  endDate: '',
  deadlineDate: '',
  active: true,
});

const cycleToFormData = (c: Cycle): CycleFormData => ({
  name: c.name,
  startDate: cycleDateToInputValue(c.startDate),
  endDate: cycleDateToInputValue(c.endDate),
  deadlineDate: cycleDateToInputValue(c.deadlineDate),
  active: c.active,
});

const Cycles: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingCycleId, setEditingCycleId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Cycle | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [formData, setFormData] = useState<CycleFormData>(emptyFormData());
  const [formSaveInProgress, setFormSaveInProgress] = useState(false);
  const [fillStatus, setFillStatus] = useState<ManagerFeedbackFillStatus | null>(null);
  const [fillStatusLoading, setFillStatusLoading] = useState(false);
  const [fillStatusError, setFillStatusError] = useState(false);
  const [selectedHeroCycleId, setSelectedHeroCycleId] = useState<string | null>(null);
  const cycleFormSurfaceRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!showForm) {
      return;
    }
    const id = window.requestAnimationFrame(() => {
      const el = cycleFormSurfaceRef.current;
      if (!el) {
        return;
      }
      /* Espaço livre abaixo do header sticky para o topo do formulário ficar visível */
      const headerGapPx = 132;
      const top = el.getBoundingClientRect().top + window.scrollY - headerGapPx;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [showForm, editingCycleId]);

  const loadCycles = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<Cycle[]>('/cycles');
      setCycles(response.data);
    } catch (error) {
      console.error('Erro ao carregar ciclos:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCycles();
  }, [loadCycles]);

  const resetFormAndClose = (): void => {
    setShowForm(false);
    setEditingCycleId(null);
    setFormData(emptyFormData());
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (formSaveInProgress) return;
    if (formData.endDate && formData.deadlineDate && formData.deadlineDate < formData.endDate) {
      setAlert({
        isOpen: true,
        message: 'O prazo de envio deve ser igual ou posterior à data de fim do ciclo.',
        type: 'error',
      });
      return;
    }
    setFormSaveInProgress(true);
    try {
      if (editingCycleId) {
        await api.patch(`/cycles/${editingCycleId}`, formData);
        setAlert({
          isOpen: true,
          message: 'Ciclo atualizado com sucesso!',
          type: 'success',
        });
      } else {
        await api.post('/cycles', formData);
        setAlert({
          isOpen: true,
          message: 'Ciclo criado com sucesso!',
          type: 'success',
        });
      }
      resetFormAndClose();
      loadCycles();
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || (editingCycleId ? 'Erro ao atualizar ciclo' : 'Erro ao criar ciclo'),
        type: 'error',
      });
    } finally {
      setFormSaveInProgress(false);
    }
  };

  const openCreateForm = (): void => {
    if (showForm) {
      resetFormAndClose();
      return;
    }
    setEditingCycleId(null);
    setFormData(emptyFormData());
    setShowForm(true);
  };

  const startEditCycle = (c: Cycle): void => {
    setEditingCycleId(c.id);
    setFormData(cycleToFormData(c));
    setShowForm(true);
  };

  const cancelDelete = (): void => {
    if (deleteInProgress) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleteInProgress(true);
    try {
      await api.delete(`/cycles/${deleteTarget.id}`);
      setDeleteTarget(null);
      if (editingCycleId === deleteTarget.id) {
        resetFormAndClose();
      }
      loadCycles();
      setAlert({ isOpen: true, message: 'Ciclo excluído.', type: 'success' });
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || 'Erro ao excluir ciclo',
        type: 'error',
      });
    } finally {
      setDeleteInProgress(false);
    }
  };

  const formatDate = (value: string): string => new Date(value).toLocaleDateString('pt-BR');

  const sortedCycles = useMemo(
    () =>
      [...cycles].sort(
        (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
      ),
    [cycles]
  );

  const activeCycle = useMemo(() => findCurrentActiveCycle(cycles), [cycles]);
  const selectedHeroCycle = useMemo(() => {
    if (selectedHeroCycleId) {
      return cycles.find((c) => c.id === selectedHeroCycleId) ?? activeCycle ?? null;
    }
    return activeCycle ?? null;
  }, [cycles, selectedHeroCycleId, activeCycle]);
  const viewingCurrentCycle = Boolean(
    selectedHeroCycle && activeCycle && selectedHeroCycle.id === activeCycle.id,
  );
  const cycleAcceptsSubmissions = Boolean(
    viewingCurrentCycle &&
      selectedHeroCycle &&
      !isCycleClosedForSubmissions(selectedHeroCycle.deadlineDate),
  );

  const heroCycleOptions = useMemo(() => {
    const currentId = activeCycle?.id;
    return [...cycles]
      .sort((a, b) => {
        if (a.id === currentId) return -1;
        if (b.id === currentId) return 1;
        return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
      })
      .map((cycle) => {
        const isCurrent = cycle.id === currentId;
        const periodEnd = formatCycleDateBr(cycle.endDate);
        return {
          value: cycle.id,
          label: `${cycle.name} · ${isCurrent ? 'Ciclo atual' : 'Encerrado'} (${periodEnd})`,
        };
      });
  }, [cycles, activeCycle?.id]);

  useEffect(() => {
    if (!cycles.length) {
      setSelectedHeroCycleId(null);
      return;
    }
    setSelectedHeroCycleId((prev) => {
      if (prev && cycles.some((c) => c.id === prev)) return prev;
      return activeCycle?.id ?? cycles[0]?.id ?? null;
    });
  }, [cycles, activeCycle?.id]);

  const canViewFillStatus =
    user?.role === 'admin' || user?.role === 'manager' || user?.role === 'hr';

  useEffect(() => {
    if (!selectedHeroCycle?.id || !canViewFillStatus) {
      setFillStatus(null);
      setFillStatusLoading(false);
      setFillStatusError(false);
      return;
    }
    let cancelled = false;
    setFillStatusError(false);
    setFillStatusLoading(true);
    void api
      .get<ManagerFeedbackFillStatus>(`/cycles/${selectedHeroCycle.id}/manager-feedback-status`)
      .then((res) => {
        if (!cancelled) {
          setFillStatus(res.data);
          setFillStatusError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFillStatus(null);
          setFillStatusError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setFillStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedHeroCycle?.id, canViewFillStatus]);

  const stats = useMemo(() => {
    const total = cycles.length;
    const activeCount = cycles.filter(isCycleCurrentlyOpen).length;
    const closed = total - activeCount;
    return { total, activeCount, closed };
  }, [cycles]);

  const deadlineTone = (deadlineDate: string): StatusTone => {
    const end = endOfCalendarDayBrazilMs(deadlineDate);
    if (Number.isNaN(end)) return 'neutral';
    const now = Date.now();
    if (end < now) return 'danger';
    const daysLeft = Math.ceil((end - now) / (86400 * 1000));
    if (daysLeft <= 7) return 'warning';
    return 'success';
  };

  const canCreate = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'hr';

  if (loading) {
    return (
      <PageContainer>
        <Stack sx={{ alignItems: 'center', justifyContent: 'center', py: 8 }} spacing={2}>
          <CircularProgress />
          <Typography variant="h6" component="h1">Preparando ciclos</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Buscando quinzenas, prazos e status dos períodos de feedback.
          </Typography>
        </Stack>
      </PageContainer>
    );
  }

  const statCards: Array<{ label: string; value: number; hint: string; icon: React.ReactNode; tone: 'neutral' | 'accent' }> = [
    {
      label: 'Total de ciclos',
      value: stats.total,
      hint: 'Histórico completo de quinzenas cadastradas na plataforma.',
      icon: <EventRepeatOutlinedIcon />,
      tone: 'neutral',
    },
    {
      label: 'Ativos agora',
      value: stats.activeCount,
      hint: 'Somente um ciclo ativo por vez; ao ativar outro, os demais são desativados automaticamente.',
      icon: <PlayCircleOutlineIcon />,
      tone: 'accent',
    },
    {
      label: 'Encerrados',
      value: stats.closed,
      hint: 'Períodos já finalizados ou desativados.',
      icon: <CheckCircleOutlineIcon />,
      tone: 'neutral',
    },
  ];

  return (
    <PageContainer
      icon={<EventRepeatOutlinedIcon />}
      maxWidth="xl"
      title="Ciclos de feedback"
      subtitle="Organize quinzenas de avaliação, controle prazos de entrega e mantenha um único ciclo ativo visível para novos feedbacks."
      actions={
        canCreate ? (
          <Button
            variant={showForm ? 'outlined' : 'contained'}
            color="primary"
            startIcon={showForm ? <CloseIcon /> : <AddIcon />}
            onClick={openCreateForm}
          >
            {showForm ? 'Fechar formulário' : 'Novo ciclo'}
          </Button>
        ) : undefined
      }
    >
      <Stack spacing={{ xs: 2, md: 3 }}>
        {/* Hero / ciclo selecionado */}
        <Paper sx={{ p: { xs: 2, md: 3 }, border: (t) => `1px solid ${t.palette.divider}` }}>
          {selectedHeroCycle ? (
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ alignItems: { sm: 'center' } }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 52,
                    height: 52,
                    display: { xs: 'none', sm: 'inline-flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: viewingCurrentCycle ? 'primary.main' : 'action.selected',
                    color: viewingCurrentCycle ? 'primary.contrastText' : 'text.secondary',
                    '& svg': { fontSize: 28 },
                  }}
                >
                  <EventRepeatOutlinedIcon />
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
                    <Typography component="span" sx={KICKER_SX}>
                      {viewingCurrentCycle ? 'Ciclo atual' : 'Ciclo selecionado'}
                    </Typography>
                    <StatusChip
                      label={viewingCurrentCycle ? (cycleAcceptsSubmissions ? 'Aberto' : 'Envios encerrados') : 'Encerrado'}
                      tone={viewingCurrentCycle ? (cycleAcceptsSubmissions ? 'success' : 'neutral') : 'neutral'}
                    />
                  </Stack>
                  {heroCycleOptions.length > 1 ? (
                    <Box sx={{ maxWidth: 480 }}>
                      <PremiumSelect
                        id="cycles-hero-cycle"
                        label="Ver ciclo"
                        value={selectedHeroCycle.id}
                        options={heroCycleOptions}
                        onChange={setSelectedHeroCycleId}
                      />
                    </Box>
                  ) : (
                    <Typography variant="h5" component="strong" sx={{ fontWeight: 800 }}>
                      {selectedHeroCycle.name}
                    </Typography>
                  )}
                </Box>
                {user?.role === 'manager' && viewingCurrentCycle && cycleAcceptsSubmissions ? (
                  <Button
                    component={Link}
                    to="/feedbacks/create"
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
                  >
                    Novo feedback
                  </Button>
                ) : null}
              </Stack>

              {/* Key dates strip */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 1.5,
                }}
              >
                {[
                  { label: 'Início', value: formatCycleDateBr(selectedHeroCycle.startDate), tone: 'neutral' as StatusTone },
                  { label: 'Fim do período', value: formatCycleDateBr(selectedHeroCycle.endDate), tone: 'neutral' as StatusTone },
                  { label: 'Prazo de envio', value: formatCycleDateBr(selectedHeroCycle.deadlineDate), tone: deadlineTone(selectedHeroCycle.deadlineDate) },
                ].map((item) => (
                  <Box key={item.label} sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                    <Typography component="span" sx={KICKER_SX}>
                      {item.label}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                        {item.value}
                      </Typography>
                      {item.label === 'Prazo de envio' && item.tone !== 'neutral' ? (
                        <StatusChip
                          label={item.tone === 'danger' ? 'Vencido' : 'Próximo'}
                          tone={item.tone}
                        />
                      ) : null}
                    </Stack>
                  </Box>
                ))}
              </Box>

              {!viewingCurrentCycle ? (
                <Typography variant="body2" color="text.secondary">
                  Você está visualizando um ciclo anterior. O ciclo atual continua sendo{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {activeCycle?.name || 'o período em vigor'}
                  </Box>
                  .
                </Typography>
              ) : null}
              {user?.role === 'manager' && viewingCurrentCycle && !cycleAcceptsSubmissions ? (
                <Typography variant="body2" color="text.secondary">
                  Envios encerrados após o prazo limite. Um administrador pode ampliar o prazo de
                  envio para reabrir.
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 52,
                  height: 52,
                  display: { xs: 'none', sm: 'inline-flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'action.selected',
                  color: 'text.secondary',
                  '& svg': { fontSize: 28 },
                }}
              >
                <EventRepeatOutlinedIcon />
              </Box>
              <Box>
                <Typography component="span" sx={KICKER_SX}>Status</Typography>
                <Typography variant="h6" component="strong" sx={{ fontWeight: 800, display: 'block' }}>
                  Nenhum ciclo ativo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ative um ciclo ou crie um novo para liberar feedbacks neste período.
                </Typography>
              </Box>
            </Stack>
          )}
        </Paper>

        {/* Estatísticas */}
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {statCards.map((card) => (
            <Grid size={{ xs: 12, md: 4 }} key={card.label}>
              <Paper
                sx={{
                  p: { xs: 2, md: 3 },
                  height: '100%',
                  border: (t) => `1px solid ${t.palette.divider}`,
                }}
              >
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1 }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 40,
                      height: 40,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: card.tone === 'accent' ? 'primary.main' : 'action.hover',
                      color: card.tone === 'accent' ? 'primary.contrastText' : 'text.secondary',
                      '& svg': { fontSize: 22 },
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography component="span" sx={KICKER_SX}>
                      {card.label}
                    </Typography>
                    <Typography variant="h4" component="strong" sx={{ fontWeight: 800, display: 'block', lineHeight: 1.1 }}>
                      {card.value}
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {card.hint}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Status de preenchimento gestor → colaborador */}
        {canViewFillStatus && selectedHeroCycle && (
          <Paper
            component="section"
            aria-labelledby="fill-status-heading"
            sx={{ p: { xs: 2, md: 3 }, border: (t) => `1px solid ${t.palette.divider}` }}
          >
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: '0.08em' }}
              >
                {viewingCurrentCycle ? 'Ciclo atual' : 'Ciclo encerrado'}
              </Typography>
              <Typography variant="h5" component="h2" id="fill-status-heading" sx={{ fontWeight: 800 }}>
                Preenchimento gestor → colaborador
                {selectedHeroCycle.name ? ` · ${selectedHeroCycle.name}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Por colaborador: recebimento do feedback do gestor e envio do feedback reverso ao gestor. Linhas em
                vermelho: falta algum dos dois na última semana antes do prazo (ou após o prazo), até concluir.
              </Typography>
            </Stack>

            {fillStatusLoading ? (
              <Typography variant="body2" color="text.secondary">Carregando status…</Typography>
            ) : fillStatusError ? (
              <Typography variant="body2" color="error">
                Não foi possível carregar o status. Atualize a página ou tente novamente em instantes.
              </Typography>
            ) : fillStatus && fillStatus.rows.length > 0 ? (
              <TableContainer component={Paper} sx={{ overflowX: 'auto', border: (t) => `1px solid ${t.palette.divider}` }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Colaborador</TableCell>
                      <TableCell>Gestor</TableCell>
                      <TableCell>Recebeu do gestor</TableCell>
                      <TableCell>Enviou ao gestor (reverso)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fillStatus.rows.map((row) => {
                      const urgent = shouldHighlightTeamFeedbackRow(
                        fillStatus.deadlineDate,
                        row.filledAt,
                        row.reverseFilledAt,
                      );
                      return (
                        <TableRow
                          key={`${row.developerId}-${row.managerId}`}
                          sx={
                            urgent
                              ? { backgroundColor: (t) => t.palette.error.light }
                              : undefined
                          }
                        >
                          <TableCell>{row.developerName}</TableCell>
                          <TableCell>{row.managerName}</TableCell>
                          <TableCell>
                            {row.filledAt ? (
                              <StatusChip label={formatDateTimeBr(row.filledAt)} tone="success" />
                            ) : (
                              <StatusChip label="Pendente" tone={urgent ? 'danger' : 'warning'} />
                            )}
                          </TableCell>
                          <TableCell>
                            {row.reverseFilledAt ? (
                              <StatusChip label={formatDateTimeBr(row.reverseFilledAt)} tone="success" />
                            ) : (
                              <StatusChip label="Pendente" tone={urgent ? 'danger' : 'warning'} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhum colaborador com gestor atribuído para exibir neste ciclo.
              </Typography>
            )}
          </Paper>
        )}

        {/* Formulário de cadastro / edição */}
        {showForm && canCreate && (
          <Paper
            component="section"
            ref={cycleFormSurfaceRef as React.Ref<HTMLDivElement>}
            aria-label={editingCycleId ? 'Formulário de edição de ciclo' : 'Formulário de novo ciclo'}
            sx={{ p: { xs: 2, md: 3 }, border: (t) => `1px solid ${t.palette.divider}` }}
          >
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: '0.08em' }}
              >
                Cadastro
              </Typography>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
                {editingCycleId ? 'Editar ciclo' : 'Criar novo ciclo'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editingCycleId
                  ? 'Altere nome, datas ou status. Se marcar como ativo, os outros ciclos serão desativados.'
                  : 'Defina o nome, o intervalo da quinzena e o prazo limite para gestores e desenvolvedores concluírem os feedbacks.'}
              </Typography>
            </Stack>

            <Box component="form" onSubmit={handleSubmit} aria-busy={formSaveInProgress}>
              <Grid container spacing={{ xs: 2, md: 3 }}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    id="cycle-name"
                    label="Nome do ciclo *"
                    type="text"
                    fullWidth
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex.: Sprint 01 · Jan 2026"
                    required
                    disabled={formSaveInProgress}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    id="cycle-start"
                    label="Data de início *"
                    type="date"
                    fullWidth
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                    disabled={formSaveInProgress}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    id="cycle-end"
                    label="Data de fim *"
                    type="date"
                    fullWidth
                    value={formData.endDate}
                    onChange={(e) => {
                      const endDate = e.target.value;
                      setFormData((prev) => {
                        const previousDefault = prev.endDate
                          ? defaultDeadlineFromEndDate(prev.endDate)
                          : '';
                        const keepCustom =
                          Boolean(prev.deadlineDate) &&
                          prev.deadlineDate !== previousDefault &&
                          (!endDate || prev.deadlineDate >= endDate);
                        return {
                          ...prev,
                          endDate,
                          deadlineDate:
                            keepCustom && prev.deadlineDate
                              ? prev.deadlineDate
                              : defaultDeadlineFromEndDate(endDate),
                        };
                      });
                    }}
                    required
                    disabled={formSaveInProgress}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    id="cycle-deadline"
                    label="Prazo de envio *"
                    type="date"
                    fullWidth
                    value={formData.deadlineDate}
                    onChange={(e) => setFormData({ ...formData, deadlineDate: e.target.value })}
                    required
                    disabled={formSaveInProgress}
                    helperText="Padrão: 3 dias corridos após a data de fim. Envios liberados até o fim deste dia (horário de Brasília)."
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: { min: formData.endDate || undefined },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <PremiumSelect
                    id="cycle-active"
                    label="Marcar como ciclo ativo"
                    value={formData.active ? 'true' : 'false'}
                    options={[
                      { value: 'true', label: 'Sim — período em vigor (desativa os demais)' },
                      { value: 'false', label: 'Não — cadastro ou arquivo' },
                    ]}
                    onChange={(value) =>
                      setFormData({ ...formData, active: value === 'true' })
                    }
                    disabled={formSaveInProgress}
                  />
                </Grid>
              </Grid>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ mt: 3, justifyContent: 'flex-end' }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={() => resetFormAndClose()}
                  disabled={formSaveInProgress}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formSaveInProgress}
                  aria-busy={formSaveInProgress}
                  startIcon={
                    formSaveInProgress ? <CircularProgress size={16} color="inherit" /> : undefined
                  }
                >
                  {formSaveInProgress
                    ? editingCycleId
                      ? 'Salvando…'
                      : 'Criando…'
                    : editingCycleId
                      ? 'Salvar alterações'
                      : 'Criar ciclo'}
                </Button>
              </Stack>
            </Box>
          </Paper>
        )}

        {/* Histórico de ciclos */}
        <Paper
          component="section"
          sx={{ p: { xs: 2, md: 3 }, border: (t) => `1px solid ${t.palette.divider}` }}
        >
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 700, letterSpacing: '0.08em' }}
            >
              Histórico
            </Typography>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
              Quinzenas cadastradas
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sortedCycles.length === 0
                ? 'Nenhum ciclo cadastrado ainda.'
                : `${sortedCycles.length} ciclo(s) encontrado(s)`}
            </Typography>
          </Stack>

          {sortedCycles.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Nada por aqui
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {canCreate
                  ? 'Crie o primeiro ciclo para iniciar a coleta de feedbacks da equipe.'
                  : 'Aguarde a liderança configurar um ciclo para este período.'}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ overflowX: 'auto', border: (t) => `1px solid ${t.palette.divider}` }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ciclo</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Início</TableCell>
                    <TableCell>Fim</TableCell>
                    <TableCell>Prazo</TableCell>
                    <TableCell>Última atualização</TableCell>
                    {canCreate ? <TableCell align="right">Ações</TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCycles.map((cycle) => {
                    const isCurrent = Boolean(activeCycle && cycle.id === activeCycle.id);
                    return (
                      <TableRow key={cycle.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {cycle.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Criado em {formatDate(cycle.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            label={isCurrent ? 'Ciclo atual' : 'Encerrado'}
                            tone={isCurrent ? 'accent' : 'neutral'}
                          />
                        </TableCell>
                        <TableCell>{formatCycleDateBr(cycle.startDate)}</TableCell>
                        <TableCell>{formatCycleDateBr(cycle.endDate)}</TableCell>
                        <TableCell>
                          <StatusChip
                            label={formatCycleDateBr(cycle.deadlineDate)}
                            tone={deadlineTone(cycle.deadlineDate)}
                          />
                        </TableCell>
                        <TableCell>{formatDate(cycle.updatedAt)}</TableCell>
                        {canCreate ? (
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{ justifyContent: 'flex-end' }}
                            >
                              <IconButton
                                aria-label={`Editar ${cycle.name}`}
                                onClick={() => startEditCycle(cycle)}
                                size="small"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                aria-label={`Excluir ${cycle.name}`}
                                onClick={() => setDeleteTarget(cycle)}
                                size="small"
                                color="error"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir ciclo"
        message={
          deleteTarget
            ? `Excluir permanentemente o ciclo «${deleteTarget.name}»? Só é permitido se não houver feedbacks vinculados.`
            : ''
        }
        confirmLabel={deleteInProgress ? 'Excluindo…' : 'Excluir'}
        onConfirm={() => void confirmDelete()}
        onCancel={cancelDelete}
        confirmDisabled={deleteInProgress}
      />
    </PageContainer>
  );
};

export default Cycles;
